/**
 * 分页响应接口
 */
export interface PaginatedResponse<T> {
    totalRows: number;
    items: T[];
}

/**
 * 包含page字段的参数接口
 */
export interface PaginatedParams {
    page: number;
    [key: string]: any;
}

/**
 * 请求函数类型定义
 */
export type RequestFunction<TParams extends PaginatedParams, TItem> = (
    params: TParams
) => Promise<PaginatedResponse<TItem>>;

/**
 * 自动获取所有分页数据的工具函数
 *
 * @param requestFn 请求函数，参数必须包含page字段，返回值为{totalRows: number, items: T[]}
 * @param initialParams 初始参数对象，必须包含page字段
 * @returns Promise<T[]> 所有页的items聚合数组
 *
 * @example
 * ```typescript
 * // 定义API请求函数
 * const fetchUsers = (params: {page: number, size: number}) =>
 *   api.get<{totalRows: number, items: User[]}>('/users', {params});
 *
 * // 使用工具函数获取所有用户
 * const allUsers = await fetchAllPages(fetchUsers, {page: 1, size: 20});
 * ```
 */
export async function fetchAllPages<
    TParams extends PaginatedParams,
    TItem
>(
    requestFn: RequestFunction<TParams, TItem>,
    initialParams: TParams
): Promise<TItem[]> {

    const allItems: TItem[] = [];
    let currentPage = initialParams.page || 1;
    let totalRows = 0;

    do {

        // 发起当前页的请求
        const response = await requestFn({
            ...initialParams,
            page: currentPage
        } as TParams );

        // 将当前页的数据添加到结果数组
        allItems.push( ...response.items );

        // 更新总数据量
        totalRows = response.totalRows;

        // 移动到下一页
        currentPage++;

        // 如果当前已获取的数据量达到总量，则停止请求

    } while ( allItems.length < totalRows );

    return allItems;

}
